/**
 * OnboardingContext
 *
 * Manages page-based tooltip state with backend persistence and localStorage cache.
 * Simple model: tips are shown based on DOM elements, dismissed tips are tracked.
 *
 * State:
 * - enabled: boolean - Are tooltips enabled globally? (false = "Skip Tour" clicked)
 * - dismissedTips: string[] - Tips user has dismissed via "Got it!"
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { settingsService } from '../services/settings';
import { tokenManager } from '../services/api';
import { debounce } from '../utils/debounce';

// LocalStorage keys (used as instant cache)
const STORAGE_KEY_ENABLED = 'careercatalyst_onboarding_enabled';
const STORAGE_KEY_DISMISSED = 'careercatalyst_onboarding_dismissed';

export interface OnboardingState {
  enabled: boolean;
  dismissedTips: string[];
}

export interface OnboardingContextValue extends OnboardingState {
  dismissTip: (tipId: string) => void;
  skipAll: () => void;
  reset: () => void;
  isTipDismissed: (tipId: string) => boolean;
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [enabled, setEnabled] = useState<boolean>(true);
  const [dismissedTips, setDismissedTips] = useState<string[]>([]);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  // Ref to track if we're currently saving to avoid loops
  const isSavingRef = useRef<boolean>(false);

  // Save state to localStorage (instant cache)
  const saveToLocalStorage = useCallback((state: OnboardingState) => {
    try {
      localStorage.setItem(STORAGE_KEY_ENABLED, String(state.enabled));
      localStorage.setItem(STORAGE_KEY_DISMISSED, JSON.stringify(state.dismissedTips));
    } catch (error) {
      console.error('Failed to save onboarding state to localStorage:', error);
    }
  }, []);

  // Load state from localStorage (instant cache)
  const loadFromLocalStorage = useCallback(() => {
    try {
      const savedEnabled = localStorage.getItem(STORAGE_KEY_ENABLED);
      const savedDismissed = localStorage.getItem(STORAGE_KEY_DISMISSED);

      if (savedEnabled !== null) {
        setEnabled(savedEnabled === 'true');
      }

      if (savedDismissed) {
        setDismissedTips(JSON.parse(savedDismissed));
      }

      setIsInitialized(true);
    } catch (error) {
      console.error('Failed to load onboarding state from localStorage:', error);
      setIsInitialized(true);
    }
  }, []);

  // Load state from backend API
  const loadFromBackend = useCallback(async () => {
    try {
      const settings = await settingsService.getOnboardingSettings();

      if (settings) {
        // Handle both old and new data formats
        // Cast to any for backward compatibility checking
        const s = settings as any;
        const newEnabled = 'enabled' in s
          ? s.enabled
          : !(s.skipped || s.completed);
        const newDismissed = 'dismissedTips' in s
          ? s.dismissedTips
          : [...(s.completedSteps || []), ...(s.disabledSteps || [])];

        setEnabled(newEnabled);
        setDismissedTips(newDismissed);

        // Update localStorage cache
        saveToLocalStorage({ enabled: newEnabled, dismissedTips: newDismissed });

        setIsInitialized(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to load onboarding settings from backend:', error);
      return false;
    }
  }, [saveToLocalStorage]);

  // Save state to backend (debounced)
  const saveToBackendImpl = useCallback(async (state: OnboardingState) => {
    if (!tokenManager.getToken()) {
      return;
    }

    try {
      isSavingRef.current = true;
      await settingsService.updateOnboardingSettings({
        enabled: state.enabled,
        dismissedTips: state.dismissedTips,
      });
    } catch (error) {
      console.error('Failed to save onboarding settings to backend:', error);
    } finally {
      isSavingRef.current = false;
    }
  }, []);

  // Debounced backend save (1.5 seconds)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const saveToBackendDebounced = useCallback(
    debounce((state: OnboardingState) => {
      saveToBackendImpl(state);
    }, 1500),
    [saveToBackendImpl]
  );

  // Load state on mount - try backend first, fallback to localStorage
  useEffect(() => {
    const initializeState = async () => {
      if (tokenManager.getToken()) {
        const loadedFromBackend = await loadFromBackend();
        if (!loadedFromBackend) {
          loadFromLocalStorage();
        }
      } else {
        loadFromLocalStorage();
      }
    };

    initializeState();
  }, [loadFromBackend, loadFromLocalStorage]);

  // Save state whenever it changes (after initialization)
  useEffect(() => {
    if (!isInitialized || isSavingRef.current) return;

    const state = { enabled, dismissedTips };

    // Save to localStorage immediately
    saveToLocalStorage(state);

    // Save to backend (debounced)
    saveToBackendDebounced(state);
  }, [enabled, dismissedTips, isInitialized, saveToLocalStorage, saveToBackendDebounced]);

  // Dismiss a specific tip
  const dismissTip = useCallback((tipId: string) => {
    setDismissedTips(prev => {
      if (prev.includes(tipId)) return prev;
      return [...prev, tipId];
    });
  }, []);

  // Skip all tooltips (disable globally)
  const skipAll = useCallback(() => {
    setEnabled(false);
  }, []);

  // Reset - clear dismissed tips and re-enable
  const reset = useCallback(() => {
    setEnabled(true);
    setDismissedTips([]);

    // Clear localStorage
    localStorage.removeItem(STORAGE_KEY_ENABLED);
    localStorage.removeItem(STORAGE_KEY_DISMISSED);
    // Also clear old keys for migration
    localStorage.removeItem('careercatalyst_onboarding_completed');
    localStorage.removeItem('careercatalyst_onboarding_skipped');
    localStorage.removeItem('careercatalyst_onboarding_current_step');
    localStorage.removeItem('careercatalyst_onboarding_completed_steps');
    localStorage.removeItem('careercatalyst_onboarding_disabled_steps');

    // Save to backend immediately
    if (tokenManager.getToken()) {
      settingsService.updateOnboardingSettings({
        enabled: true,
        dismissedTips: [],
      }).catch(console.error);
    }
  }, []);

  // Check if a tip is dismissed
  const isTipDismissed = useCallback((tipId: string): boolean => {
    return dismissedTips.includes(tipId);
  }, [dismissedTips]);

  const contextValue: OnboardingContextValue = {
    enabled,
    dismissedTips,
    dismissTip,
    skipAll,
    reset,
    isTipDismissed,
  };

  return (
    <OnboardingContext.Provider value={contextValue}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboardingContext = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboardingContext must be used within OnboardingProvider');
  }
  return context;
};
